import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft } from 'lucide-react';
import { env } from '../config/env';
import { oauth2Config } from '../config/oauth2.config';

export const ForgotPasswordPage: React.FC = () => {
  const { forgotPassword, isLoading } = useAuth();

  const [email, setEmail] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    try {
      await forgotPassword(email);
      setSuccessMessage('Password reset instructions have been sent to your email address.');
      setEmail('');
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.response?.data?.message || 'Failed to send password reset instructions. Please try again.';
      setError(message);
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] flex flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-[480px] space-y-10">
        <div className="text-center space-y-4">
          <Link to="/" className="flex flex-col items-center gap-4">
            {env.LOGO_URL ? (
              <img src={env.LOGO_URL} alt={env.COMPANY_NAME} className="h-12 w-auto object-contain" />
            ) : (
              <div className="size-12 bg-primary rounded-xl flex items-center justify-center text-white">
                <span className="material-symbols-outlined text-3xl">shopping_bag</span>
              </div>
            )}
            <h1 className="text-3xl font-black tracking-tight uppercase text-[#111418] dark:text-white">
              {env.COMPANY_NAME}
            </h1>
          </Link>
          <p className="text-sm font-medium text-gray-500 max-w-xs mx-auto">Reset your password</p>
        </div>

        <div className="bg-white dark:bg-black p-10 md:p-12 rounded-[2.5rem] border border-border/40 shadow-2xl shadow-primary/5 space-y-8">
          {successMessage ? (
            <div className="text-green-600 text-center text-sm font-medium bg-green-50 p-4 rounded-lg">
              {successMessage}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-semibold">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12 rounded-xl border border-gray-200 dark:border-gray-700 focus-visible:ring-2 focus-visible:ring-primary/50"
                  />
                </div>

                {error && (
                  <div className="text-red-500 text-sm font-medium">{error}</div>
                )}

                <Button
                  type="submit"
                  disabled={isLoading}
                  size="lg"
                  className="w-full h-14 rounded-2xl text-base font-black shadow-xl shadow-primary/10 transition-all hover:-translate-y-0.5 bg-[#111418] dark:bg-white text-white dark:text-black flex items-center justify-center gap-3 border-0"
                >
                  {isLoading ? 'Sending instructions...' : 'Send Reset Instructions'}
                </Button>
              </div>
            </form>
          )}
        </div>

        <div className="flex items-center justify-center gap-6">
          <Link to={`/login?client_id=${oauth2Config.clientId}`} className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 hover:text-primary transition-colors flex items-center gap-2">
            <ArrowLeft className="size-3" /> Back to login
          </Link>
        </div>
      </div>
    </div>
  );
};